/**
 * Niche alcoves in 3D. An alcove with a raised base, or a ceiling of its own
 * below the room's, leaves the wall walk: its parent wall runs straight across
 * with the mouth punched through as an opening at the alcove's index, so 3D
 * picking still selects the alcove. The room's floor slab skips a raised
 * niche and its ceiling slab a lowered one, and the recess behind the cutout
 * carries its own walls and slabs.
 */
import {
  alcoveRingVerts,
  EDGE_NAMES,
  edgeFrame,
  type EdgeName,
  edgeSegments,
  type FloorPlanEdgeSegment,
  type FloorPlanRoom,
  type Point,
  roomCeilingHeight,
  WALL_THICKNESS,
} from '@kaiizen/kai-floor-plan';
import { PALETTE } from '../editor-model';
import { type Vec3 } from '../math3d';
import { type MeshBuilder } from './mesh3d';
import { buildBox, type PlanToWorld, ringSides } from './sceneBox';

/** How far above the floor, or below the ceiling, an alcove's base or top must sit to make it a niche (ft). */
const NICHE_TOLERANCE = 0.01;

/** An alcove's vertical span [base, top], resolved against the ceiling. */
function alcoveSpan(segment: FloorPlanEdgeSegment, ceiling: number): { base: number; top: number } {
  const base = Math.max(0, segment.openingBase ?? 0);
  return { base, top: segment.openingHeight != null ? Math.min(ceiling, base + segment.openingHeight) : ceiling };
}

const isRaised = (segment: FloorPlanEdgeSegment, ceiling: number) =>
  segment.edgeSegmentType === 'alcove' && alcoveSpan(segment, ceiling).base > NICHE_TOLERANCE;
const isLowered = (segment: FloorPlanEdgeSegment, ceiling: number) =>
  segment.edgeSegmentType === 'alcove' && alcoveSpan(segment, ceiling).top < ceiling - NICHE_TOLERANCE;
const isNiche = (segment: FloorPlanEdgeSegment, ceiling: number) => isRaised(segment, ceiling) || isLowered(segment, ceiling);

/** The room with each edge segment `matches` accepts swapped for `replacement`'s segments, or the room itself when none matches. */
function replaceSegments(
  shape: FloorPlanRoom,
  matches: (segment: FloorPlanEdgeSegment) => boolean,
  replacement: (segment: FloorPlanEdgeSegment) => FloorPlanEdgeSegment[],
): FloorPlanRoom {
  if (!EDGE_NAMES.some(edge => edgeSegments(shape, edge).some(matches))) return shape;
  const replaced = { ...shape };
  for (const edge of EDGE_NAMES) {
    replaced[edge] = { segments: edgeSegments(shape, edge).flatMap(segment => (matches(segment) ? replacement(segment) : [segment])) };
  }
  return replaced;
}

/** The opening a niche's mouth cuts through its parent wall, over the niche's span. */
function nicheMouth(segment: FloorPlanEdgeSegment, ceiling: number): FloorPlanEdgeSegment {
  const { base, top } = alcoveSpan(segment, ceiling);
  return { edgeSegmentType: 'opening', position: segment.position, width: segment.width, openingBase: base, openingHeight: top - base };
}

/**
 * The room as each part of the scene builds it: the floor slab without its
 * raised niches, the ceiling slab without its lowered ones, and the walls
 * with every niche's mouth as an opening. A part no niche changes is the room
 * itself.
 */
export function nicheShapes(shape: FloorPlanRoom): { floor: FloorPlanRoom; ceiling: FloorPlanRoom; walls: FloorPlanRoom } {
  const ceiling = roomCeilingHeight(shape);
  return {
    floor: replaceSegments(shape, segment => isRaised(segment, ceiling), () => []),
    ceiling: replaceSegments(shape, segment => isLowered(segment, ceiling), () => []),
    walls: replaceSegments(shape, segment => isNiche(segment, ceiling), segment => [nicheMouth(segment, ceiling)]),
  };
}

/** The niche's ring with its mouth feet (vertices 0 and 5, on the wall line) pushed out to the parent wall's outer face. */
function nicheRing(shape: FloorPlanRoom, edge: EdgeName, segment: FloorPlanEdgeSegment): Point[] {
  const { perp } = edgeFrame(edge, shape.width, shape.length);
  const ring = alcoveRingVerts(shape, edge, segment);
  const toOuterFace = (point: Point): Point => ({ x: point.x + perp.x * WALL_THICKNESS, y: point.y + perp.y * WALL_THICKNESS });
  return [toOuterFace(ring[0]), ...ring.slice(1, 5), toOuterFace(ring[5])];
}

/**
 * The recess behind one niche's cutout: its walls over the niche's span on
 * every ring side but the mouth, facing into the recess, and its own floor
 * slab (raised base) and ceiling slab (lowered ceiling). Everything joins the
 * parent wall's cull group, so the niche shows and hides with that wall. The
 * recess starts at the parent wall's outer face, since the cutout's reveals
 * already line the first wall thickness of its depth.
 */
function buildNiche(mesh: MeshBuilder, shape: FloorPlanRoom, edge: EdgeName, segment: FloorPlanEdgeSegment, toWorld: PlanToWorld): void {
  const ceiling = roomCeilingHeight(shape);
  const { base, top } = alcoveSpan(segment, ceiling);
  if (top - base < 1e-3) return;
  const ring = nicheRing(shape, edge, segment);
  const group = EDGE_NAMES.indexOf(edge);
  mesh.solid({ layer: 'wall', rings: [ring], closed: false, base, top });
  mesh.begin(group);
  for (const { start, end, facing } of ringSides(ring, true).slice(0, -1)) {
    if (Math.hypot(end.x - start.x, end.y - start.y) < 1e-4) continue;
    const corners: [Vec3, Vec3, Vec3, Vec3] = [
      toWorld(start.x, start.y, base),
      toWorld(end.x, end.y, base),
      toWorld(end.x, end.y, top),
      toWorld(start.x, start.y, top),
    ];
    mesh.quad(...corners, facing, PALETTE.wall);
    mesh.polyline(corners, true);
  }
  mesh.end();
  if (base > NICHE_TOLERANCE) buildBox(mesh, [ring], base - WALL_THICKNESS, base, PALETTE.floor, toWorld, 'floor', group);
  if (top < ceiling - NICHE_TOLERANCE) buildBox(mesh, [ring], top, top + WALL_THICKNESS, PALETTE.ceiling, toWorld, 'ceiling', group);
}

/** Builds the recess behind every niche alcove (see buildNiche). */
export function buildNiches(mesh: MeshBuilder, shape: FloorPlanRoom, toWorld: PlanToWorld): void {
  for (const edge of EDGE_NAMES) {
    for (const segment of edgeSegments(shape, edge)) if (isNiche(segment, roomCeilingHeight(shape))) buildNiche(mesh, shape, edge, segment, toWorld);
  }
}
